<template>
    <div class="deposit-refund-header">
        <h4 class="deposit-refund-header__title">Đề xuất hoàn phí bổ trợ cấp tốc</h4>
        <div class="deposit-refund-header__actions">
            <button class="btn btn-primary font-weight-bold" type="button" @click="$emit('openImport')">
                <i class="el-icon-plus"></i> Import
            </button>
            <el-button
                :loading="isExporting"
                class="btn-export text-white font-weight-bold"
                type="button"
                @click="exportDepositRefund"
            >
                <i class="el-icon-download"></i>
                <span v-if="!isExporting">Export</span>
                <span v-else>{{ percent }}%</span>
            </el-button>
        </div>
    </div>
</template>

<script>
import {exportExcelMultipleSheet} from "@/core/filters";
import {GET_LIST_DEPOSIT_REFUND} from "@/core/services/store/deposit-refund/depositRefund.module";
import {DepositRefundModel, STATUS_OPTIONS} from "../model/DepositRefundModel";

const STATUS_EXPORT_COLUMNS = [
    {key: "value", value: "Mã trạng thái"},
    {key: "label", value: "Trạng thái"},
];

const EXPORT_COLUMNS = [
    {key: "id", value: "Id"},
    {key: "contractCode", value: "Mã HĐ"},
    {key: "studentName", value: "Học viên"},
    {key: "studentCode", value: "Mã học viên"},
    {key: "phone", value: "Số điện thoại"},
    {key: "mainClassName", value: "Lớp chính"},
    {key: "supplementaryClass1Name", value: "Lớp BTCT 1"},
    {key: "supplementaryClass2Name", value: "Lớp BTCT 2"},
    {key: "absenceCount", value: "Số buổi vắng"},
    {key: "homeworkMissingCount", value: "Số buổi không làm BTVN"},
    {key: "totalErrors", value: "Tổng lỗi"},
    {key: "refundAmountDisplay", value: "Tiền cần hoàn"},
    {key: "processingStatusLabel", value: "Trạng thái"},
    {key: "processingStatusCode", value: "Mã trạng thái"},
    {key: "bankAccountNumber", value: "Số tài khoản"},
    {key: "bankAccountName", value: "Chủ tài khoản"},
    {key: "bankName", value: "Ngân hàng"},
];

export default {
    name: "HeaderDepositRefundList",
    data() {
        return {
            isExporting: false,
            percent: 0,
        };
    },
    methods: {
        parseStatusFromQuery(status) {
            if (status === undefined || status === null || status === "") return null;
            if (Array.isArray(status)) {
                const arr = status.map((s) => Number(s)).filter((n) => !Number.isNaN(n));
                return arr.length ? arr : null;
            }
            try {
                const arr = JSON.parse(`[${status}]`)
                    .map((s) => Number(s))
                    .filter((n) => !Number.isNaN(n));
                return arr.length ? arr : null;
            } catch {
                const n = Number(status);
                return Number.isNaN(n) ? null : [n];
            }
        },
        parseListPage(res) {
            if (res?.error) return null;
            if (res?.pagination) {
                return {
                    rows: Array.isArray(res.data) ? res.data : [],
                    last_page: Number(res.pagination.last_page || 1),
                };
            }
            const pageObj = res?.data?.data ? res.data : res?.data;
            const rows = pageObj?.data ?? (Array.isArray(pageObj) ? pageObj : []);
            return {
                rows: Array.isArray(rows) ? rows : [],
                last_page: Number(pageObj?.last_page || 1),
            };
        },
        formatCurrency(amount) {
            const num = Number(amount);
            if (Number.isNaN(num)) return amount ?? "—";
            return `${num.toLocaleString("vi-VN")} đ`;
        },
        mapExportRow(item) {
            return {
                id: item.id,
                contractCode: item.contractCode,
                studentName: item.studentName || "—",
                studentCode: item.studentCode || "—",
                phone: item.phone || "—",
                mainClassName: item.mainClassName || "—",
                supplementaryClass1Name: item.supplementaryClass1Name || "—",
                supplementaryClass2Name: item.supplementaryClass2Name || "—",
                absenceCount: item.absenceCount,
                homeworkMissingCount: item.homeworkMissingCount,
                totalErrors: item.totalErrors,
                refundAmountDisplay: this.formatCurrency(item.refundAmount),
                processingStatusLabel: item.processingStatusLabel,
                processingStatusCode: item.processingStatus,
                bankAccountNumber: item.bankAccountNumber || "—",
                bankAccountName: item.bankAccountName || "—",
                bankName: item.bankName || "—",
            };
        },
        buildExportPayload(page) {
            const q = this.$route.query || {};
            return {
                page,
                per_page: 1000,
                keyword: q.keyword ?? null,
                status: this.parseStatusFromQuery(q.status),
                from_date: q.from_date ?? null,
                to_date: q.to_date ?? null,
            };
        },
        async exportDepositRefund() {
            this.isExporting = true;
            try {
                const firstRes = await this.$store.dispatch(
                    GET_LIST_DEPOSIT_REFUND,
                    this.buildExportPayload(1)
                );
                const firstPage = this.parseListPage(firstRes);
                if (!firstPage) {
                    this.$bvToast.toast(firstRes?.message || "Export thất bại", {
                        title: "Export excel",
                        variant: "danger",
                        solid: true,
                    });
                    return;
                }

                const totalPages = firstPage.last_page;
                let exportRows = firstPage.rows.map((row) =>
                    this.mapExportRow(new DepositRefundModel(row))
                );
                this.percent = totalPages > 0 ? Math.round((1 / totalPages) * 100) : 0;

                for (let page = 2; page <= totalPages; page++) {
                    const pageRes = await this.$store.dispatch(
                        GET_LIST_DEPOSIT_REFUND,
                        this.buildExportPayload(page)
                    );
                    const pageData = this.parseListPage(pageRes);
                    if (!pageData) continue;
                    exportRows = [
                        ...exportRows,
                        ...pageData.rows.map((row) => this.mapExportRow(new DepositRefundModel(row))),
                    ];
                    this.percent = Math.min(100, Math.round((page / totalPages) * 100));
                }

                this.percent = 100;
                const statusRows = STATUS_OPTIONS.map((item) => ({
                    value: item.value,
                    label: item.label,
                }));
                exportExcelMultipleSheet(
                    [
                        {
                            header: EXPORT_COLUMNS,
                            data: exportRows,
                            sheet: "Danh sách",
                        },
                        {
                            header: STATUS_EXPORT_COLUMNS,
                            data: statusRows,
                            sheet: "Danh sách trạng thái",
                        },
                    ],
                    "Đề xuất hoàn phí bổ trợ cấp tốc.xlsx",
                    false
                );
            } catch {
                this.$bvToast.toast("Export thất bại", {
                    title: "Export excel",
                    variant: "danger",
                    solid: true,
                });
            } finally {
                this.isExporting = false;
                setTimeout(() => {
                    this.percent = 0;
                }, 500);
            }
        },
    },
};
</script>

<style scoped>
.deposit-refund-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    gap: 16px;
    flex-wrap: wrap;
}

.deposit-refund-header__title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #181c32;
}

.deposit-refund-header__actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.btn-export {
    background: #00b5b8 !important;
    border-color: #00b5b8 !important;
    padding: 8px 16px;
}

.btn-export:hover,
.btn-export:focus {
    background: #009a9d !important;
    border-color: #009a9d !important;
}
</style>
